import {MutableRefObject, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {PlaylistItem} from '../../backend/interfaces/playlist-item';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {CaregiverMode} from '../../enums';
import {usePaginatedTable} from '../../hooks/paginated-table';
import {translations} from '../../i18n/translations';
import {PaginatedTableData} from '../../interfaces/paginated-table-data';
import {PlaylistsRepository} from '../../repositories/playlists';
import {PaginatedTable} from '../paginated-table/paginated-table';
import {PlaylistItemStateModal} from './modal/playlist-item-state-modal';
import {Box, Stack} from '@mui/material';
import './user-playlists.scss';
import GenericHeader from '../generic-header/generic-header';
import SearchInput from '../search-input/search-input';

// Columns IDs
enum ColumnIdentifier {
  Title = 'title',
  State = 'state',
}

/**
 * Listing of all the playlist items associated with a user.
 * On this screen, it's possible to enable/disable a playlist item.
 *
 * Note: This is only used by professional caregivers at the moment.
 */
type UserPlaylistItemsProps = {
  caregiverMode: CaregiverMode.Professional | CaregiverMode.SystemAdministrator;
  canWrite: boolean;
  userId: string;
  userName: string;
  entityId: string;
  playlistId: string;
  playlistItemRowBaseLinkUrl: string;
  headerTitleCrumbs?: Array<{label: string; link: string}>;
  isLoadingHeader?: boolean;
};
export const UserPlaylistItems = ({
  caregiverMode,
  canWrite,
  userId,
  userName,
  entityId,
  playlistId,
  playlistItemRowBaseLinkUrl,
  headerTitleCrumbs,
  isLoadingHeader,
}: UserPlaylistItemsProps) => {
  const snackBarContext = useSnackBar();
  // Hold state
  const [updateItemDialogOpened, setUpdateItemDialogOpened] = useState<{
    disableDialogOpened: boolean;
    enableDialogOpened: boolean;
  }>({disableDialogOpened: false, enableDialogOpened: false});

  // Hold ref
  const dataToUpdateRef: MutableRefObject<
    {itemId: string; itemName: string; itemActiveFlag: boolean} | undefined
  > = useRef();
  const {t} = useTranslation();

  const handleGetItems = async (limit: number, skip: number, search?: string) => {
    // Load playlist items
    const result: {totalCount: number; data: Array<PlaylistItem>} =
      await PlaylistsRepository.instance.getPlaylistItemsForUserInEntityAsAffiliatedForEntity(
        userId,
        entityId,
        playlistId,
        skip,
        limit,
        true,
        search
      );

    // Process data
    const processedData: PaginatedTableData<PlaylistItem> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.id || '',
        item: obj,
        enabled: obj.isActive,
        avatarUrl: obj.thumbnailUrl,
        toLink:
          playlistItemRowBaseLinkUrl.charAt(playlistItemRowBaseLinkUrl.length - 1) === '/'
            ? `${playlistItemRowBaseLinkUrl}${obj.id}`
            : `${playlistItemRowBaseLinkUrl}/${obj.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Title,
            // The title is localized
            value: obj.title || '',
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.isActive
              ? t(translations.user_playlist_items.state_value_true)
              : t(translations.user_playlist_items.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateCurrentSearch,
    handleUpdateItem,
  } = usePaginatedTable<PlaylistItem>({
    fetchData: handleGetItems,
  });

  const handleAskUpdateItemState = (itemId: string, value: boolean) => {
    // Get item name
    const itemName: string | undefined = data
      .find((obj) => obj.id === itemId)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Title)?.value;

    // Set item info for later
    dataToUpdateRef.current = {
      itemId: itemId,
      itemName: itemName || '',
      itemActiveFlag: value,
    };

    // Open modal
    setUpdateItemDialogOpened({
      disableDialogOpened: !value,
      enableDialogOpened: value,
    });
  };

  const handleUpdateItemState = () => {
    if (dataToUpdateRef.current) {
      // Keep values
      const itemId = dataToUpdateRef.current.itemId;
      const value = dataToUpdateRef.current.itemActiveFlag;

      // Clear
      dataToUpdateRef.current = undefined;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === itemId)?.item;
      if (updatedItem) {
        updatedItem.isActive = value;
      }

      // Update item in UI
      handleUpdateItem(
        itemId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: value
              ? t(translations.user_playlist_items.state_value_true)
              : t(translations.user_playlist_items.state_value_false),
          },
        ],
        updatedItem,
        value
      );

      // Make request to the backend
      // Note: Don't await because it will take too much time to update
      continueUpdateItemState(userId, playlistId, itemId, value);
    } else {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.user_playlist_items.errors.update_state_item_error_message),
      });

      // Not expected. Log.
      console.debug(
        '🔴 Could not update the state of the playlist item. Information was missing to perform the request.'
      );
    }
  };

  const continueUpdateItemState = async (
    userId: string,
    playlistId: string,
    playlistItemId: string,
    value: boolean
  ) => {
    try {
      // Make request to the backend
      await PlaylistsRepository.instance.updatePlaylistItemActiveStateForUserAsAffiliatedForEntity(
        userId,
        playlistId,
        playlistItemId,
        value
      );
    } catch (error) {
      // Old flag
      const oldFlag: boolean = !value;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === playlistItemId)?.item;
      if (updatedItem) {
        updatedItem.isActive = oldFlag;
      }

      // Change item back
      handleUpdateItem(
        playlistItemId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: oldFlag
              ? t(translations.user_playlist_items.state_value_true)
              : t(translations.user_playlist_items.state_value_false),
          },
        ],
        updatedItem,
        oldFlag
      );

      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: value
          ? t(translations.user_playlist_items.errors.enable_item_error_message)
          : t(translations.user_playlist_items.errors.disable_item_error_message),
      });

      // Log
      console.debug(
        `🔴 Could not update the state of the playlist item with id ${playlistItemId} to ${value} for user with id ${userId}.`,
        error
      );
    }
  };

  const modalState: 'disable' | 'enable' = updateItemDialogOpened.disableDialogOpened
    ? 'disable'
    : 'enable';
  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      {headerTitleCrumbs && (
        <GenericHeader
          titleCrumbs={headerTitleCrumbs}
          isLoading={!!isLoadingHeader}
          leftColumn={
            <Stack
              gap={2}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                paddingTop: '10px',
              }}
            >
              <SearchInput
                placeholder={t(translations.user_playlist_items.search_input_placeholder)}
                value={currentSearch}
                onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
                onSearchAction={(value: string) => handleApplyFilters(1, value)}
              />
            </Stack>
          }
          rightColumn={<></>}
        />
      )}
      <Box className="playlists-listing-container">
        <Box className="playlists-listing-container-centered">
          <PaginatedTable
            avatar={{
              type: 'other',
            }}
            emptyTableMessage={t(translations.user_playlist_items.no_items)}
            columns={[
              {
                columnId: ColumnIdentifier.Title,
                title: t(translations.user_playlist_items.title_header),
                flexGrow: 2,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(translations.user_playlist_items.state_header),
                flexGrow: 1,
                button: {
                  type: 'toggle',
                  showColumnValue: true,
                  toggle: {
                    enabledValue: t(translations.user_playlist_items.state_value_true),
                    handleClick: handleAskUpdateItemState,
                  },
                  disabled: !canWrite,
                  disabledTooltip: t(translations.user_playlist_items.state_disabled_tooltip),
                },
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={caregiverMode !== CaregiverMode.Professional}
          />
          <PlaylistItemStateModal
            open={
              updateItemDialogOpened.disableDialogOpened ||
              updateItemDialogOpened.enableDialogOpened
            }
            onClose={() => {
              setUpdateItemDialogOpened({
                disableDialogOpened: false,
                enableDialogOpened: false,
              });
              dataToUpdateRef.current = undefined;
            }}
            onConfirm={() => {
              setUpdateItemDialogOpened({
                disableDialogOpened: false,
                enableDialogOpened: false,
              });

              handleUpdateItemState();
            }}
            state={modalState}
            playlistName={dataToUpdateRef.current?.itemName || ''}
            userName={userName}
          />
        </Box>
      </Box>
    </Box>
  );
};
